// L'inscription de Radar en trois écrans (lot 2 de la refonte, 2026-10-11).
//
// ELLE REMPLACE LA PRÉSENTATION EN SEPT ÉTAPES (`premierPassage.tsx`, retirée), où le
// CV n'arrivait qu'à l'étape 5, après une vidéo et deux écrans sur
// l'extension. Un invité qui découvre l'outil décide en cinq minutes, et rien
// ne marche sans CV : on commence donc par lui. Trois questions, dans cet
// ordre, et chacune a un effet visible tout de suite :
//
//  1. le CV, et les métiers que Radar y lit (corrigeables d'un geste) ;
//  2. les employeurs, triés par « offres dans vos métiers » : un simple
//     croisement des catégories déjà posées, sans aucun appel IA ;
//  3. le rythme des mails, chiffré sur les offres réelles du compte.
//
// L'extension n'est plus ici : elle vit sous « Aide et extension », où l'on
// vient la chercher quand on en a l'usage. Sauf si elle est DÉJÀ installée et
// porte un CV (issue #234) : on propose alors de le reprendre, d'un clic. La région, prévue par la maquette,
// n'est pas encore là : aucun filtre ne s'en servirait, et une question sans
// effet apprend à ne pas lire les suivantes.
//
// PAGE ENTIÈRE, PAS MODALE. On ne voit rien derrière une inscription : un
// Radar vide en fond disait « rien ne marche » au moment exact où l'on
// demande de la confiance.

import { useEffect, useRef, useState } from 'react'
import { Check, FileUp, ChevronLeft, Radar as IconeRadar } from 'lucide-react'
import { get, post, put, type SourceRow } from '../lib/api'
import { useData, useToast, ActionButton, estUnPdf } from '../components/common'
import { useT } from '../lib/i18n'
import { signalerTache } from '../components/bandeauTaches'
import { connecterLExtension, cvDeLExtension, useExtensionInstallee } from '../lib/extensionInstallee'
import { ChoixMetiers, type Choix, type CategorieAssignee } from '../components/categories'
import { LogoEntreprise } from '../components/sites'
import { ChoixAdresse, ChoixRegions, ChoixRythme, type EtatRythme, type Region, type Rythme } from '../components/rythme'

type Etape = 1 | 2 | 3

export function Bienvenue({
  profileId,
  plafond,
  onProfilCree,
  onFini,
}: {
  profileId: number | null
  plafond?: number
  /** Le premier CV crée le profil ET le rattache à la session. */
  onProfilCree: () => void
  onFini: () => void
}) {
  const t = useT()
  const toast = useToast()
  const aUnProfil = (profileId ?? -1) > 0
  const [etape, setEtape] = useState<Etape>(1)
  const metiers = useRef<Choix[] | null>(null)
  const [choisis, setChoisis] = useState<Set<number> | null>(null)
  const [rythme, setRythme] = useState<Rythme>('equilibre')
  const [regions, setRegions] = useState<Region[]>([])

  async function quitterEtape1() {
    if (aUnProfil && regions.length > 0) {
      try {
        await post('/api/radar/rythme', { regions })
      } catch (e: any) {
        toast(e?.message ?? String(e), 'err')
        return
      }
    }
    if (metiers.current && aUnProfil) {
      try {
        await put(`/api/consultants/${profileId}/categories`, { codes: metiers.current.map((c) => c.code) })
        metiers.current = null
        signalerTache()
      } catch (e: any) {
        toast(e?.message ?? String(e), 'err')
        return
      }
    }
    setEtape(2)
  }

  async function terminer() {
    try {
      await post('/api/radar/rythme', { rythme })
      await post('/api/auth/onboarded')
      signalerTache()
    } catch (e: any) {
      toast(e?.message ?? String(e), 'err')
      return
    }
    onFini()
  }

  return (
    <div className="bvn">
      <div className="bvn-col">
        <div className="bvn-tete">
          <span className="bvn-marque"><span className="logo"><IconeRadar size={16} /></span>Radar</span>
          <span className="bvn-etape">{t('bienvenue.etape', { n: etape })}</span>
        </div>
        <div className="bvn-barre" aria-hidden="true"><span style={{ width: `${(etape / 3) * 100}%` }} /></div>

        {etape === 1 && (
          <EtapeCv
            profileId={profileId}
            onProfilCree={onProfilCree}
            onMetiers={(c) => { metiers.current = c }}
            regions={regions}
            setRegions={setRegions}
            onSuivant={quitterEtape1}
          />
        )}
        {etape === 2 && (
          <EtapeEmployeurs
            plafond={plafond}
            choisis={choisis}
            setChoisis={setChoisis}
            onRetour={() => setEtape(1)}
            onSuivant={async (ids) => {
              // Enregistrés DÈS cette étape : le rythme qui suit se chiffre sur
              // les sites suivis, et le rattrapage commence à les lire.
              try {
                await put('/api/sources/abonnements', { sources: [...ids] })
                signalerTache()
              } catch (e: any) {
                toast(e?.message ?? String(e), 'err')
                return
              }
              setEtape(3)
            }}
          />
        )}
        {etape === 3 && <EtapeRythme rythme={rythme} setRythme={setRythme} onRetour={() => setEtape(2)} onFin={terminer} />}
      </div>
    </div>
  )
}

/** Toutes les deux secondes pendant une minute : le temps que le rattrapage
 *  lancé au dépôt du CV ait rangé le profil (même règle que l'ancienne étape). */
const RELECTURE_MS = 2000
const RELECTURES_MAX = 30

function EtapeCv({
  profileId,
  onProfilCree,
  onMetiers,
  regions,
  setRegions,
  onSuivant,
}: {
  profileId: number | null
  onProfilCree: () => void
  onMetiers: (c: Choix[]) => void
  regions: Region[]
  setRegions: (r: Region[]) => void
  onSuivant: () => Promise<void>
}) {
  const t = useT()
  const toast = useToast()
  const aUnProfil = (profileId ?? -1) > 0
  const [envoi, setEnvoi] = useState(false)
  const [survol, setSurvol] = useState(false)
  const [nom, setNom] = useState<string | null>(null)
  const [choix, setChoix] = useState<Choix[] | null>(null)
  const [attente, setAttente] = useState(true)
  const touche = useRef(false)
  const champ = useRef<HTMLInputElement>(null)
  // L'extension du store porte peut-être déjà le CV (issue #234).
  const extension = useExtensionInstallee()

  async function reprendreDeLExtension() {
    const profile = await cvDeLExtension()
    if (!profile) { toast(t('bienvenue.cv.reprendreEchec'), 'err'); return }
    const r = await post<{ name: string }>('/api/consultants/import-extension', { profile })
    setNom(r.name)
    signalerTache()
    onProfilCree()
    // Le CV est dans Radar : l'extension peut s'y connecter tout de suite,
    // et devient la copie de ce CV. Un échec ne bloque rien, l'aide le refait.
    void connecterLExtension().then((c) => { if (c.ok) toast(t('ext.connectee')) }, () => undefined)
  }

  async function deposer(fichier: File | undefined) {
    if (!fichier || envoi) return
    if (!estUnPdf(fichier)) { toast(t('importCv.needFile'), 'err'); return }
    setEnvoi(true)
    try {
      const form = new FormData()
      form.append('file', fichier)
      form.append('name', '')
      const r = await post<{ name: string }>('/api/consultants/import', form)
      setNom(r.name)
      signalerTache()
      onProfilCree()
    } catch (e: any) {
      toast(e?.message ?? String(e), 'err')
    } finally {
      setEnvoi(false)
    }
  }

  // Les métiers arrivent quelques secondes après le dépôt : on relit jusqu'à
  // les voir, sans jamais écraser une correction déjà faite.
  useEffect(() => {
    if (!aUnProfil) return
    let essais = 0
    let fini = false
    const lire = () =>
      get<CategorieAssignee[]>(`/api/consultants/${profileId}/categories`)
        .then((r) => {
          if (touche.current) return
          if (r.length > 0 || ++essais >= RELECTURES_MAX) {
            fini = true
            setAttente(false)
          }
          setChoix(r.map((c) => ({ code: c.code, label: c.label })))
        })
        .catch(() => undefined)
    void lire()
    const id = setInterval(() => { if (!fini && !touche.current) void lire() }, RELECTURE_MS)
    return () => clearInterval(id)
  }, [profileId, aUnProfil])

  return (
    <>
      <h1 className="bvn-titre">{t('bienvenue.cv.titre')}</h1>
      <p className="bvn-sous">{t('bienvenue.cv.sous')}</p>
      {/* L'essai se dit dès l'inscription (#112) : un mois, puis payant. */}
      <p className="bvn-sous">{t('bienvenue.essai')}</p>

      {!aUnProfil && extension?.cv && (
        <div className="bvn-carte">
          <p className="bvn-lu"><Check size={16} />{t('bienvenue.cv.extension')}</p>
          <div>
            <ActionButton variant="primary" onClick={reprendreDeLExtension}>
              {t('bienvenue.cv.reprendre')}
            </ActionButton>
          </div>
        </div>
      )}
      {!aUnProfil ? (
        <label
          className={`bvn-depot${survol ? ' survol' : ''}${envoi ? ' envoi' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setSurvol(true) }}
          onDragLeave={() => setSurvol(false)}
          onDrop={(e) => { e.preventDefault(); setSurvol(false); void deposer(e.dataTransfer.files?.[0]) }}
        >
          <input ref={champ} type="file" accept="application/pdf" hidden disabled={envoi} onChange={(e) => void deposer(e.target.files?.[0])} />
          {envoi ? <span className="spinner big" /> : <FileUp size={28} />}
          <b>{envoi ? t('bienvenue.cv.lecture') : t('bienvenue.cv.depot')}</b>
          {!envoi && <span className="bvn-ou">{t('bienvenue.cv.ou')}</span>}
        </label>
      ) : (
        <div className="bvn-carte">
          <p className="bvn-lu"><Check size={16} />{nom ? t('bienvenue.cv.luNom', { nom }) : t('bienvenue.cv.lu')}</p>
          <div>
            <div className="bvn-eyebrow">{t('bienvenue.cv.metiers')}</div>
            {attente && !touche.current && (choix ?? []).length === 0 && <p className="field-help">{t('accueil.metiers.enCours')}</p>}
            <ChoixMetiers
              choix={choix ?? []}
              onChange={(c) => { touche.current = true; setChoix(c); onMetiers(c) }}
            />
            <p className="field-help">{t('bienvenue.cv.metiersAide')}</p>
          </div>
          <div>
            <div className="bvn-eyebrow">{t('regions.question')}</div>
            <ChoixRegions choisies={regions} onChange={setRegions} />
            <p className="field-help">{t('regions.aide')}</p>
          </div>
        </div>
      )}

      {/* Pas de bouton tant qu'aucun CV n'est lu : « C'est bien moi » ne
          voudrait rien dire, et un bouton grisé se lit comme une panne. */}
      {aUnProfil && (
        <div className="bvn-pied">
          <ActionButton variant="primary" onClick={onSuivant}>
            {t('bienvenue.cv.suivant')}
          </ActionButton>
        </div>
      )}
    </>
  )
}

function EtapeEmployeurs({
  plafond,
  choisis,
  setChoisis,
  onRetour,
  onSuivant,
}: {
  plafond?: number
  choisis: Set<number> | null
  setChoisis: (s: Set<number>) => void
  onRetour: () => void
  onSuivant: (ids: Set<number>) => Promise<void>
}) {
  const t = useT()
  const toast = useToast()
  const sources = useData(() => get<SourceRow[]>('/api/sources'), [])
  const tous = (sources.data ?? []).filter((s) => !s.archived_at && s.platform !== 'extension' && s.platform !== 'manual')
  const parMetiers = (a: SourceRow, b: SourceRow) =>
    (b.dans_metiers ?? 0) - (a.dans_metiers ?? 0) || b.offers - a.offers || (a.company ?? a.name).localeCompare(b.company ?? b.name)
  const employeurs = tous.filter((s) => !s.annonces).sort(parMetiers)
  const annonces = tous.filter((s) => s.annonces).sort(parMetiers)

  // Premier passage : les abonnements RÉELS, ou à défaut les sites d'annonces
  // cochés d'avance. Mesure du 2026-10-09 : ils portent 70 % des offres à 70
  // et plus. Un employeur, lui, n'est jamais coché pour quelqu'un.
  const actuels = choisis ?? (() => {
    const deja = tous.filter((s) => Boolean(s.abonne)).map((s) => s.id)
    return new Set(deja.length > 0 ? deja : annonces.map((s) => s.id))
  })()

  function basculer(s: SourceRow) {
    if (!actuels.has(s.id) && plafond !== undefined && actuels.size >= plafond) {
      toast(t('accueil.sites.plafond', { max: plafond }), 'err')
      return
    }
    const apres = new Set(actuels)
    if (apres.has(s.id)) apres.delete(s.id)
    else apres.add(s.id)
    setChoisis(apres)
  }

  const tuile = (s: SourceRow) => {
    const coche = actuels.has(s.id)
    const n = s.dans_metiers ?? 0
    return (
      <button key={s.id} type="button" role="checkbox" aria-checked={coche} className={`bvn-tuile${coche ? ' on' : ''}`} onClick={() => basculer(s)}>
        <LogoEntreprise nom={s.company ?? s.name} logo={s.logo} className="gs-logo" />
        <b>{s.company ?? s.name}</b>
        <span className={n > 0 ? 'bvn-n' : 'bvn-n zero'}>{n > 0 ? t('bienvenue.emp.dansMetiers', { n }) : t('bienvenue.emp.rien')}</span>
        <span className="gs-coche" aria-hidden="true"><Check size={12} strokeWidth={3} /></span>
      </button>
    )
  }

  return (
    <>
      <h1 className="bvn-titre">{t('bienvenue.emp.titre')}</h1>
      <p className="bvn-sous">{plafond === undefined ? t('bienvenue.emp.sousSansMax') : t('bienvenue.emp.sous', { max: plafond })}</p>

      {sources.loading ? (
        <span className="spinner" />
      ) : (
        <>
          {annonces.length > 0 && (
            <section className="bvn-carte bvn-annonces">
              <div className="bvn-eyebrow">{t('bienvenue.emp.annoncesTitre')}</div>
              <p className="field-help">{t('bienvenue.emp.annoncesAide')}</p>
              <div className="bvn-tuiles">{annonces.map(tuile)}</div>
            </section>
          )}
          <div className="bvn-eyebrow">{t('bienvenue.emp.employeursTitre')}</div>
          <div className="bvn-tuiles">{employeurs.map(tuile)}</div>
          <p className="field-help">{t('bienvenue.emp.manque')}</p>
        </>
      )}

      <div className="bvn-pied colle">
        <button type="button" className="btn-secondary" onClick={onRetour}><ChevronLeft size={15} />{t('bienvenue.retour')}</button>
        <span className="grow bvn-compte">
          {plafond === undefined ? t('accueil.sites.count', { n: actuels.size }) : t('accueil.sites.countMax', { n: actuels.size, max: plafond })}
        </span>
        <ActionButton
          variant="primary"
          disabled={actuels.size === 0}
          onClick={async () => { setChoisis(actuels); await onSuivant(actuels) }}
        >
          {t('bienvenue.continuer')}
        </ActionButton>
      </div>
    </>
  )
}

function EtapeRythme({
  rythme,
  setRythme,
  onRetour,
  onFin,
}: {
  rythme: Rythme
  setRythme: (r: Rythme) => void
  onRetour: () => void
  onFin: () => Promise<void>
}) {
  const t = useT()
  const etat = useData(() => get<EtatRythme>('/api/radar/rythme'), [])
  // Un compte neuf n'a encore rien de noté : Radar compare ses offres pendant
  // qu'il lit cet écran. Trois « 0 offre » diraient que Radar ne trouvera rien,
  // ce qui est faux : on n'affiche pas de chiffre plutôt qu'un chiffre trompeur.
  const e = etat.data
  const chiffre = e && Object.values(e.estimations).some((n) => n > 0) ? e : null
  return (
    <>
      <h1 className="bvn-titre">{t('bienvenue.rythme.titre')}</h1>
      <p className="bvn-sous">{chiffre ? t('rythme.note') : t('bienvenue.rythme.pasEncore')}</p>
      <ChoixRythme etat={chiffre} choisi={rythme} onChoisir={setRythme} />
      {etat.data?.email && <ChoixAdresse etat={etat.data} onChange={() => etat.reload()} />}
      <div className="bvn-pied">
        <button type="button" className="btn-secondary" onClick={onRetour}><ChevronLeft size={15} />{t('bienvenue.retour')}</button>
        <span className="grow" />
        <ActionButton variant="primary" onClick={onFin}>{t('bienvenue.fin')}</ActionButton>
      </div>
    </>
  )
}
